import React, { useState, useEffect } from 'react';
import {
  Show,
  TextField,
  DateField,
  ReferenceField,
  TopToolbar,
  useRecordContext,
  useRedirect,
  FunctionField,
  useDataProvider,
  useUpdate,
  useNotify,
  useDelete,
} from 'react-admin';
import ResourceBreadcrumbs from '../../components/ResourceBreadcrumbs';
import { CreateOpportunityDialog } from '../../components/CreateOpportunityDialog';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Box,
  Avatar,
  Divider,
  Paper,
  Stack,
  Button as MuiButton,
  Breadcrumbs,
  Link,
  Badge,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Tooltip,
  TextField as MuiTextField,
  InputAdornment,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  TrendingUp as OpportunityIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Assessment as ProbabilityIcon,
  Edit as EditIcon,
  ViewKanban as BoardIcon,
  NavigateNext as BreadcrumbIcon,
  CheckCircle as WonIcon,
  Cancel as LostIcon,
  Source as SourceIcon,
  ContactMail as LeadIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ShowActions = () => (
  <TopToolbar>
    {/* EditButton removed - edit functionality now in custom dialog */}
    {/* DeleteButton moved to edit dialog */}
  </TopToolbar>
);

const OpportunityBreadcrumbs = () => {
  const record = useRecordContext();
  const navigate = useNavigate();

  if (!record) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Breadcrumbs
        separator={<BreadcrumbIcon fontSize="small" />}
        sx={{
          '& .MuiBreadcrumbs-separator': {
            mx: 1,
          },
          '& .MuiBreadcrumbs-li': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/opportunities')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            textDecoration: 'none',
            color: 'primary.main',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          <BoardIcon fontSize="small" />
          Opportunity Board
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <OpportunityIcon fontSize="small" />
          {record.name}
        </Typography>
      </Breadcrumbs>
    </Box>
  );
};

const ValueField = ({ record }: { record?: any }) => {
  if (!record?.value) {
    return <span>-</span>;
  }

  return (
    <Box sx={{ fontWeight: 'bold', color: 'success.main', fontSize: '1.2rem' }}>
      ${record.value.toLocaleString()}
    </Box>
  );
};

const ProbabilityField = ({ record }: { record?: any }) => {
  if (record?.probability === null || record?.probability === undefined) {
    return <span>-</span>;
  }

  const getColor = (probability: number) => {
    if (probability >= 80) {
      return 'success';
    }
    if (probability >= 50) {
      return 'warning';
    }
    if (probability >= 25) {
      return 'info';
    }
    return 'error';
  };

  return (
    <Chip label={`${record.probability}%`} size="medium" color={getColor(record.probability)} />
  );
};

const StageField = ({ record }: { record?: any }) => {
  if (!record?.stage?.name && !record?.stageId) {
    return <span>-</span>;
  }

  const stageText = record.stage?.name || 'Unknown';

  const getStageColor = (stage: string) => {
    switch (stage.toLowerCase()) {
      case 'prospecting':
        return 'info';
      case 'qualification':
        return 'primary';
      case 'proposal':
        return 'warning';
      case 'negotiation':
        return 'secondary';
      case 'closedwon':
        return 'success';
      case 'closedlost':
        return 'error';
      default:
        return 'default';
    }
  };

  return <Chip label={stageText} size="medium" color={getStageColor(stageText)} />;
};

const StatusField = ({ record }: { record?: any }) => {
  if (!record?.stage) {
    return <span>-</span>;
  }

  // Derive status from stage properties
  const getStatusFromStage = (stage: any) => {
    if (stage.isClosedWon) return 'Won';
    if (stage.isClosedLost) return 'Lost';
    return 'Open';
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'primary';
      case 'won':
        return 'success';
      case 'lost':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'won':
        return <WonIcon sx={{ fontSize: 16, mr: 0.5 }} />;
      case 'lost':
        return <LostIcon sx={{ fontSize: 16, mr: 0.5 }} />;
      default:
        return null;
    }
  };

  const derivedStatus = getStatusFromStage(record.stage);

  return (
    <Chip 
      label={derivedStatus}
      size="medium" 
      color={getStatusColor(derivedStatus)}
      icon={getStatusIcon(derivedStatus)}
    />
  );
};

const OpportunitySummaryCard = ({ onDelete }: { onDelete: () => void }) => {
  const record = useRecordContext();
  const redirect = useRedirect();

  if (!record) {
    return null;
  }

  return (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'primary.main',
                fontSize: '1.5rem',
                fontWeight: 'bold',
              }}
            >
              <OpportunityIcon sx={{ fontSize: 40 }} />
            </Avatar>
          </Grid>

          <Grid item xs>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ fontWeight: 'bold', color: 'primary.main' }}
            >
              {record.name}
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MoneyIcon color="success" />
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  ${record.value?.toLocaleString() || '0'}
                </Typography>
              </Box>

              {record.probability !== null && record.probability !== undefined && (
                <ProbabilityField record={record} />
              )}

              {record.stage && <StatusField record={record} />}

              {record.stage?.name && <StageField record={record} />}
            </Stack>

            <Grid container spacing={2}>
              {record.customer && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon color="action" />
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => redirect(`/customers/${record.customerId}/show`)}
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {record.customer.displayName || record.customer.company || 'Unknown Customer'}
                    </Link>
                  </Box>
                </Grid>
              )}

              {record.lead && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LeadIcon color="action" />
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => redirect(`/leads/${record.leadId}/show`)}
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {record.lead.displayName || 'Unknown Lead'}
                    </Link>
                  </Box>
                </Grid>
              )}

              {record.assignedUser && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {record.assignedUser.name}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {record.expectedCloseDate && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {new Date(record.expectedCloseDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {record.source && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SourceIcon color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {record.source}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid item>
            <Stack spacing={1}>
              <MuiButton
                variant="outlined"
                color="primary"
                startIcon={<BoardIcon />}
                onClick={() => redirect('/opportunities')}
                size="small"
              >
                Back to Board
              </MuiButton>

              <MuiButton
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={onDelete}
                size="small"
              >
                Delete Opportunity
              </MuiButton>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const QuickStatsCard = () => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  // Calculate days until close
  const daysUntilClose = record.expectedCloseDate
    ? Math.ceil(
        (new Date(record.expectedCloseDate).getTime() - new Date().getTime()) /
          (1000 * 60 * 60 * 24)
      )
    : null;

  const stats = [
    {
      label: 'Expected Value',
      value: `$${record.value?.toLocaleString() || '0'}`,
      color: 'success',
      icon: <MoneyIcon />,
    },
    {
      label: 'Probability',
      value: `${record.probability || 0}%`,
      color: 'primary',
      icon: <ProbabilityIcon />,
    },
    {
      label: 'Days to Close',
      value: daysUntilClose !== null ? `${daysUntilClose} days` : 'Not set',
      color: 'warning',
      icon: <CalendarIcon />,
    },
    {
      label: 'Stage',
      value: record.stage?.name || 'Unknown',
      color: 'info',
      icon: <OpportunityIcon />,
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {stats.map((stat, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              {React.cloneElement(stat.icon, { color: stat.color as any })}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {stat.value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {stat.label}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

const OpportunityDetailsCard = ({ dialogOpen, setDialogOpen }: { dialogOpen: boolean, setDialogOpen: (open: boolean) => void }) => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" color="primary">
            Opportunity Details
          </Typography>
          <IconButton
            size="small"
            onClick={() => setDialogOpen(true)}
            sx={{ color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={3}>
          <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
              Opportunity Name
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {record.name}
            </Typography>
          </Box>

          <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
              Value
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
              ${record.value?.toLocaleString() || '0'}
            </Typography>
          </Box>

          <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
              Probability
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {record.probability !== null && record.probability !== undefined ? `${record.probability}%` : 'Not set'}
            </Typography>
          </Box>

          {record.expectedCloseDate && (
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                Expected Close Date
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {new Date(record.expectedCloseDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}
              </Typography>
            </Box>
          )}

          {record.source && (
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                Lead Source
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {record.source}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const AssignmentCard = ({ dialogOpen, setDialogOpen }: { dialogOpen: boolean, setDialogOpen: (open: boolean) => void }) => {
  const record = useRecordContext();
  const redirect = useRedirect();

  if (!record) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" color="primary">
            Assignment & Pipeline
          </Typography>
          <IconButton
            size="small"
            onClick={() => setDialogOpen(true)}
            sx={{ color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={3}>
          {record.customer && (
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                Customer
              </Typography>
              <Link
                component="button"
                variant="body1"
                onClick={() => redirect(`/customers/${record.customerId}/show`)}
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {record.customer.displayName || record.customer.company || 'Unknown Customer'}
              </Link>
            </Box>
          )}

          {record.lead && (
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                Lead
              </Typography>
              <Link
                component="button"
                variant="body1"
                onClick={() => redirect(`/leads/${record.leadId}/show`)}
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {record.lead.displayName || 'Unknown Lead'}
              </Link>
            </Box>
          )}

          {record.assignedUser && (
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
                Assigned User
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {record.assignedUser.name}
              </Typography>
            </Box>
          )}

          <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5, fontWeight: 600 }}>
              Sales Pipeline
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Chip 
                label={record.board?.name || 'Unknown Board'} 
                size="small" 
                color="primary" 
                variant="outlined"
                icon={<BoardIcon />}
              />
              <Typography variant="body2" color="text.secondary">•</Typography>
              <StageField record={record} />
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const NotesCard = ({ dialogOpen, setDialogOpen }: { dialogOpen: boolean, setDialogOpen: (open: boolean) => void }) => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" color="primary">
            Description & Notes
          </Typography>
          <IconButton
            size="small"
            onClick={() => setDialogOpen(true)}
            sx={{ color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={2}>
          {record.description ? (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                Description
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {record.description}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              color: 'text.secondary',
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px dashed',
              borderColor: 'grey.300'
            }}>
              <Typography variant="body2">
                No description available
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const OpportunityTitle = () => {
  const record = useRecordContext();
  if (!record) return 'Loading...';
  
  return record.name || 'Opportunity';
};

const OpportunityDetailsEditDialog = ({ open, onClose, record }: { open: boolean, onClose: () => void, record: any }) => {
  const [update, { isLoading: isUpdating }] = useUpdate();
  const notify = useNotify();
  const [formData, setFormData] = useState({
    name: '',
    value: '',
    probability: 50,
    expectedCloseDate: '',
    source: '',
  });

  React.useEffect(() => {
    if (open) {
      setFormData({
        name: record?.name || '',
        value: record?.value || '',
        probability: record?.probability || 50,
        expectedCloseDate: record?.expectedCloseDate ? record.expectedCloseDate.split('T')[0] : '',
        source: record?.source || '',
      });
    }
  }, [record, open]);

  const handleSave = async () => {
    if (!record?.id) {
      notify('Cannot save: No opportunity selected', { type: 'error' });
      return;
    }

    try {
      const updateData = {
        ...formData,
        value: formData.value ? parseFloat(formData.value.toString()) : null,
        expectedCloseDate: formData.expectedCloseDate || null,
      };

      await update('opportunities', {
        id: record.id,
        data: updateData,
        previousData: record,
      });

      notify('Opportunity updated successfully', { type: 'success' });
      onClose();
    } catch (error: any) {
      notify(error.message || 'Failed to update opportunity', { type: 'error' });
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Opportunity Details</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MuiTextField
                fullWidth
                label="Opportunity Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <MuiTextField
                fullWidth
                label="Value"
                type="number"
                value={formData.value}
                onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                helperText="Expected revenue from this opportunity"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography gutterBottom>
                Probability: {formData.probability}%
              </Typography>
              <Slider
                value={formData.probability}
                onChange={(e, newValue) => setFormData(prev => ({ ...prev, probability: newValue as number }))}
                step={5}
                marks
                min={0}
                max={100}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value}%`}
              />
            </Grid>

            <Grid item xs={12}>
              <MuiTextField
                fullWidth
                label="Expected Close Date"
                type="date"
                value={formData.expectedCloseDate}
                onChange={(e) => setFormData(prev => ({ ...prev, expectedCloseDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                helperText="When do you expect to close this opportunity?"
              />
            </Grid>

            <Grid item xs={12}>
              <MuiTextField
                fullWidth
                label="Lead Source"
                value={formData.source}
                onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value }))}
                helperText="How did this opportunity originate?"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isUpdating}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isUpdating || !formData.name}
        >
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Assignment & Pipeline Edit Dialog
const AssignmentPipelineEditDialog = ({ open, onClose, record }: { open: boolean, onClose: () => void, record: any }) => {
  const [update, { isLoading: isUpdating }] = useUpdate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [boards, setBoards] = useState<any[]>([]);
  const [stages, setStages] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    boardId: '',
    stageId: '',
    assignedUserId: '',
  });

  useEffect(() => {
    if (open) {
      // Load boards
      dataProvider.getList('boards', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: {},
      }).then(({ data }) => setBoards(data));

      // Load users
      dataProvider.getList('users', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: {},
      }).then(({ data }) => setUsers(data)).catch(() => {
        // If users endpoint fails, just continue
      });

      // Set initial form data
      setFormData({
        boardId: record?.boardId || '',
        stageId: record?.stageId || '',
        assignedUserId: record?.assignedUserId || '',
      });
    }
  }, [open, record, dataProvider]);

  useEffect(() => {
    // Load stages when board changes
    if (formData.boardId) {
      dataProvider.getOne('boards', { id: formData.boardId })
        .then(({ data }) => {
          setStages(data.stages || []);
        });
    }
  }, [formData.boardId, dataProvider]);

  const handleSave = async () => {
    // Validate that if a board is selected, a stage must also be selected
    if (formData.boardId && !formData.stageId) {
      notify('Please select a stage for the selected board', { type: 'error' });
      return;
    }

    try {
      await update('opportunities', {
        id: record.id,
        data: {
          boardId: formData.boardId || null,
          stageId: formData.stageId || null,
          assignedUserId: formData.assignedUserId || null,
        },
        previousData: record,
      });
      notify('Assignment updated successfully', { type: 'success' });
      onClose();
    } catch (error: any) {
      notify(error.message || 'Failed to update assignment', { type: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Assignment & Pipeline</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Sales Pipeline</InputLabel>
            <Select
              value={formData.boardId}
              onChange={(e) => setFormData({ ...formData, boardId: e.target.value, stageId: '' })}
              label="Sales Pipeline"
            >
              <MenuItem value="">None</MenuItem>
              {boards.map((board) => (
                <MenuItem key={board.id} value={board.id}>
                  {board.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {formData.boardId && (
            <FormControl fullWidth required error={formData.boardId && !formData.stageId}>
              <InputLabel required>Stage</InputLabel>
              <Select
                value={formData.stageId}
                onChange={(e) => setFormData({ ...formData, stageId: e.target.value })}
                label="Stage"
                required
              >
                {stages.length === 0 ? (
                  <MenuItem disabled value="">
                    <em>No stages available</em>
                  </MenuItem>
                ) : (
                  stages.map((stage) => (
                    <MenuItem key={stage.id} value={stage.id}>
                      {stage.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {formData.boardId && !formData.stageId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  Stage is required when a board is selected
                </Typography>
              )}
            </FormControl>
          )}

          <FormControl fullWidth>
            <InputLabel>Assigned User</InputLabel>
            <Select
              value={formData.assignedUserId}
              onChange={(e) => setFormData({ ...formData, assignedUserId: e.target.value })}
              label="Assigned User"
            >
              <MenuItem value="">None</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isUpdating}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isUpdating || (formData.boardId && !formData.stageId)}
        >
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Description & Notes Edit Dialog
const DescriptionNotesEditDialog = ({ open, onClose, record }: { open: boolean, onClose: () => void, record: any }) => {
  const [update, { isLoading: isUpdating }] = useUpdate();
  const notify = useNotify();
  const [formData, setFormData] = useState({
    description: '',
  });

  useEffect(() => {
    if (open) {
      setFormData({
        description: record?.description || '',
      });
    }
  }, [open, record]);

  const handleSave = async () => {
    try {
      await update('opportunities', {
        id: record.id,
        data: {
          description: formData.description || null,
        },
        previousData: record,
      });
      notify('Description updated successfully', { type: 'success' });
      onClose();
    } catch (error: any) {
      notify(error.message || 'Failed to update description', { type: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Description & Notes</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <MuiTextField
            label="Description"
            multiline
            rows={8}
            fullWidth
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Enter opportunity description and notes..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isUpdating}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isUpdating}>
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const OpportunityShowContent = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const [opportunityDetailsDialogOpen, setOpportunityDetailsDialogOpen] = useState(false);
  const [relationshipsDialogOpen, setRelationshipsDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const redirect = useRedirect();
  const notify = useNotify();
  const [deleteOne, { isLoading: isDeleting }] = useDelete('opportunities', { id: record?.id });

  const handleDelete = async () => {
    if (!record?.id) {
      notify('No opportunity to delete', { type: 'error' });
      return;
    }
    
    try {
      await deleteOne();
      notify('Opportunity deleted successfully', { type: 'success' });
      redirect('/opportunities');
    } catch (error) {
      notify('Error deleting opportunity', { type: 'error' });
    }
    setDeleteDialogOpen(false);
  };

  return (
    <Box>
      <OpportunitySummaryCard onDelete={() => setDeleteDialogOpen(true)} />
      <QuickStatsCard />

      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={6}>
          <OpportunityDetailsCard 
            dialogOpen={opportunityDetailsDialogOpen}
            setDialogOpen={setOpportunityDetailsDialogOpen}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <AssignmentCard 
            dialogOpen={relationshipsDialogOpen}
            setDialogOpen={setRelationshipsDialogOpen}
          />
        </Grid>

        <Grid item xs={12}>
          <NotesCard 
            dialogOpen={notesDialogOpen}
            setDialogOpen={setNotesDialogOpen}
          />
        </Grid>
      </Grid>

      {/* Opportunity Details Edit Dialog */}
      <OpportunityDetailsEditDialog
        open={opportunityDetailsDialogOpen}
        onClose={() => setOpportunityDetailsDialogOpen(false)}
        record={record || {}}
      />

      {/* Assignment & Pipeline Edit Dialog */}
      <AssignmentPipelineEditDialog
        open={relationshipsDialogOpen}
        onClose={() => setRelationshipsDialogOpen(false)}
        record={record}
      />

      {/* Description & Notes Edit Dialog */}
      <DescriptionNotesEditDialog
        open={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        record={record}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Opportunity
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete "{record?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const OpportunityShow = () => (
  <Show actions={<ShowActions />} title={<OpportunityTitle />}>
    <Box>
      <ResourceBreadcrumbs />
      <OpportunityShowContent />
    </Box>
  </Show>
);
